import { computed, linkedSignal, signal } from "@angular/core";
import { Workflow } from "../service/Workflow";
import { WorkflowService } from "../service/workflow.service";
import { ContactService } from "../../../services/contact.service";
import { UserService } from "../../../services/user.service";
import { NotificationService } from "../../../services/notification.service";
import {
    SocChatExternalType,
    SocChannelType,
    SocContactUser,
    SocPublicChannelResult,
    SocSkippedUser,
    StandoutClassroomService,
} from "../../../services/standout.classroom.service";
import {
    buildWorkflowChatChannelTitle,
    buildWorkflowChatExternalId,
    buildWorkflowPersonChatChannelTitle,
    buildWorkflowPersonChatExternalId,
    partitionWorkflowChatUsers,
    WorkflowChatType,
} from "../../../services/soc.chat.channel.util";
import { S25Date } from "../../../util/core/s25.date";
import { isUndefined } from "../../../util/core";
import { S25Assert } from "../../../util/s25.assert";

export type WorkflowChatTarget =
    | { kind: "group"; chatType: WorkflowChatType }
    | { kind: "person"; contId: number; name: string; chatUser?: Workflow.DiagramChatUser };

/**
 * State and channel creation for the workflow chat panel. One instance per host; the panel component renders it.
 * Used by the My Workflow card chat and by the workflow diagram's "Chat with {person}" on My Workflow and My Forms.
 */
export class WorkflowChatController {
    readonly target = signal<WorkflowChatTarget | undefined>(undefined);
    readonly loading = signal(false);
    readonly minimized = linkedSignal<WorkflowChatTarget | undefined, boolean>({
        source: this.target,
        computation: () => false,
    });
    readonly channelId = signal<string | null>(null);
    readonly channelType = signal<SocChannelType>("private");
    readonly socChannelId = signal<number | undefined>(undefined);
    readonly channelTitle = signal("");
    private readonly submissionUuid = signal("");
    private returnFocusTo: HTMLElement | null = null;

    readonly isOpen = computed(() => this.target() != null);

    readonly title = computed(() => {
        const target = this.target();
        if (!target) return "";
        if (target.kind === "person") return `Chat with ${target.name}`;
        switch (target.chatType) {
            case "submitter":
                return "Chat with Submitter";
            case "reviewers":
                return "Chat with Reviewers";
            case "all":
                return "Chat with Everyone";
            default:
                return S25Assert.never(target.chatType, "Unknown workflow chat type");
        }
    });

    readonly channelKey = computed(() => {
        if (this.channelType() === "direct" && this.socChannelId() != null) {
            return String(this.socChannelId());
        }
        return this.channelId() ?? this.submissionUuid();
    });

    static enrichedChannelTitle(submission: Workflow.WorkflowItem): string {
        const name = submission.name ?? "";
        const date = S25Date.toS25ISODateStr(submission.submissionDate) ?? "";
        return `${name} | Workflow ID: ${submission.itemId} | Submitted: ${date}`;
    }

    /** Opens the panel and creates (or rejoins) the channel. Focus returns to `returnFocusTo` when the chat closes. */
    async start(submission: Workflow.WorkflowItem, target: WorkflowChatTarget, returnFocusTo?: HTMLElement | null) {
        this.returnFocusTo = returnFocusTo ?? null;
        this.submissionUuid.set(submission.submissionIdUuid ?? "");
        this.loading.set(true);
        this.target.set(target);
        const isCurrent = () => this.target() === target;

        try {
            if (!submission.submissionIdUuid) throw new Error("Submission UUID is missing");
            const { externalId, title, users, skippedUsers } =
                target.kind === "person"
                    ? await this.personChannel(submission, target)
                    : await this.groupChannel(submission, target.chatType);
            if (!isCurrent()) return;

            const result = await StandoutClassroomService.createPrivateChannelFromUsers(
                users,
                externalId,
                SocChatExternalType.Workflow,
                title,
            );
            if (!isCurrent()) return;
            WorkflowChatController.notifySkippedUsers(result, skippedUsers);
            this.channelId.set(result.externalId);
            this.channelTitle.set(title);
            this.channelType.set(result.channelType);
            this.socChannelId.set(result.socChannelId);
            void StandoutClassroomService.forceRefreshUnreadChats();
        } catch (caughtError) {
            if (!isCurrent()) return;
            console.error("Error starting chat:", caughtError);
            this.target.set(undefined);
            const message =
                caughtError instanceof Error ? caughtError.message : "Unable to open chat. Please try again.";
            NotificationService.post(message);
            this.restoreFocus();
        } finally {
            if (isCurrent()) this.loading.set(false);
        }
    }

    close() {
        this.target.set(undefined);
        this.loading.set(false);
        this.channelId.set(null);
        this.channelTitle.set("");
        this.channelType.set("private");
        this.socChannelId.set(undefined);
        this.restoreFocus();
    }

    private restoreFocus() {
        const element = this.returnFocusTo;
        this.returnFocusTo = null;
        if (element?.isConnected) element.focus();
    }

    private async personChannel(
        submission: Workflow.WorkflowItem,
        target: Extract<WorkflowChatTarget, { kind: "person" }>,
    ) {
        const currentContId = await ContactService.getCurrentId();
        if (isUndefined(currentContId)) throw new Error("Unable to determine current user for chat.");
        const { chatUser } = target;
        if (!chatUser?.username) throw new Error(`${target.name} does not have a 25Live login`);
        const users: SocContactUser[] = [
            {
                userType: "25live",
                contId: target.contId,
                username: chatUser.username,
                firstname: chatUser.firstName ?? "",
                lastname: chatUser.lastName ?? "",
                email: chatUser.email ?? "",
            },
        ];
        return {
            externalId: buildWorkflowPersonChatExternalId(submission.submissionIdUuid!, currentContId, target.contId),
            title: buildWorkflowPersonChatChannelTitle(
                WorkflowChatController.enrichedChannelTitle(submission),
                target.name,
            ),
            users,
            skippedUsers: [] as SocSkippedUser[],
        };
    }

    private async groupChannel(submission: Workflow.WorkflowItem, chatType: WorkflowChatType) {
        const { workflowProcessInstanceId, currentWorkflowStageId, contId } = submission;
        if (!workflowProcessInstanceId || !currentWorkflowStageId) {
            throw new Error("Missing process instance or stage ID");
        }

        const currentContId = await ContactService.getCurrentId();
        if (chatType === "submitter" && isUndefined(currentContId)) {
            throw new Error("Unable to determine current user for chat.");
        }

        let reviewers: Workflow.WorkflowR25User[] = [];
        let submitterUser: Awaited<ReturnType<typeof UserService.getUserByContactId>> = null;
        if (chatType === "reviewers") {
            reviewers = await WorkflowService.getStageReviewers(workflowProcessInstanceId, currentWorkflowStageId);
        } else if (chatType === "submitter") {
            if (!contId) throw new Error("Submitter contact ID is missing");
            submitterUser = await UserService.getUserByContactId(contId);
        } else {
            if (!contId) throw new Error("Submitter contact ID is missing");
            [reviewers, submitterUser] = await Promise.all([
                WorkflowService.getStageReviewers(workflowProcessInstanceId, currentWorkflowStageId),
                UserService.getUserByContactId(contId),
            ]);
        }

        const { users, skippedUsers } = partitionWorkflowChatUsers(
            chatType,
            reviewers,
            submitterUser,
            contId ?? undefined,
        );
        if (!users.length) {
            if (chatType === "reviewers") throw new Error("No reviewers with 25Live accounts found");
            if (chatType === "submitter") throw new Error("Submitter does not have a 25Live login");
            throw new Error("No users with 25Live accounts found");
        }

        return {
            externalId: buildWorkflowChatExternalId(submission.submissionIdUuid!, chatType, currentContId),
            title: buildWorkflowChatChannelTitle(WorkflowChatController.enrichedChannelTitle(submission), chatType),
            users,
            skippedUsers,
        };
    }

    private static notifySkippedUsers(result: SocPublicChannelResult, clientSkipped: SocSkippedUser[] = []) {
        const allSkipped = [...clientSkipped, ...(result.skippedUsers ?? [])];
        if (!allSkipped.length) return;
        const items = allSkipped.map((user) => `<li>${user.username ?? user.contId} — ${user.reason}</li>`).join("");
        NotificationService.post(
            `Some users could not be added to the chat.<details style="margin-top:6px"><summary style="cursor:pointer">${allSkipped.length} skipped user(s) — click for details</summary><ul style="margin-top:4px; padding-left:1.5em">${items}</ul></details>`,
        );
    }
}
